import React from 'react';
import './Button.css';

/**
 * Tennis Diary UI Kit — Button
 *
 * @example
 *   <Button>Save</Button>
 *   <Button variant="secondary" size="sm">Cancel</Button>
 *   <Button variant="danger" leftIcon={<Trash />}>Delete</Button>
 *   <Button loading>Submitting…</Button>
 *   <Button as="a" href="/help" variant="link">Read the docs</Button>
 *
 * Props
 * - variant:  'primary' | 'secondary' | 'ghost' | 'danger' | 'link'   (default: 'primary')
 * - size:     'sm' | 'md' | 'lg'                                      (default: 'md')
 * - block:    boolean — full-width
 * - loading:  boolean — replaces leftIcon with spinner, disables button
 * - leftIcon, rightIcon: ReactNode
 * - iconOnly: boolean — visually-only icon button (use `aria-label`)
 * - as:       'button' | 'a' | React component                        (default: 'button')
 */
export const Button = React.forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    block = false,
    loading = false,
    disabled = false,
    leftIcon = null,
    rightIcon = null,
    iconOnly = false,
    as: Tag = 'button',
    type,
    className = '',
    ...rest
  },
  ref,
) {
  const classes = [
    'tdk-btn',
    `tdk-btn--${variant}`,
    `tdk-btn--${size}`,
    block ? 'tdk-btn--block' : '',
    iconOnly ? 'tdk-btn--icon-only' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const isNativeButton = Tag === 'button';
  const isDisabled = disabled || loading;

  return (
    <Tag
      ref={ref}
      className={classes}
      type={isNativeButton ? type || 'button' : type}
      disabled={isNativeButton ? isDisabled : undefined}
      aria-disabled={!isNativeButton && isDisabled ? true : undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="tdk-btn__spinner" aria-hidden="true" />
      ) : (
        leftIcon
      )}
      {!iconOnly && children}
      {!loading && rightIcon}
    </Tag>
  );
});

export default Button;
